<template>
  <div>
    <div class="page-head">
      <button type="button" class="entry" @click="$router.push('/student/catalog')">← Back to catalog</button>
      <h1 style="margin-top:12px">{{ subjectName }}</h1>
      <p>{{ subject.dsa_subject_code }} · {{ subjectCategory(subject) }}</p>
    </div>
    <dsa-loading v-if="loading" text="Loading subject…" />
    <template v-else-if="subject">
      <div class="card" style="margin-bottom:20px">
        <div class="meta-grid">
          <div><div class="label">Window</div><div class="val">{{ subject.window_label || subject.dsa_quarter_code || '—' }}</div></div>
          <div><div class="label">Exam date</div><div class="val">{{ subject.exam_date || '—' }}</div></div>
          <div><div class="label">Testing</div><div class="val">{{ subject.exam_window_label || '—' }}</div></div>
          <div><div class="label">Attempts</div><div class="val">{{ attemptCount }} / {{ maxAttempts }}</div></div>
        </div>
        <p v-if="subject.project_intro || subject.project_desc || subject.short_intro" class="desc">
          {{ subject.project_intro || subject.project_desc || subject.short_intro }}
        </p>
        <p v-else class="desc muted">No additional description for this subject.</p>
      </div>

      <div class="actions">
        <span v-if="registered" class="chip open">Already enrolled this window</span>
        <span v-else-if="!canRegister" class="chip pend">Max {{ maxAttempts }} attempts reached</span>
        <button
          v-else
          type="button"
          class="btn btn-primary"
          @click="openRegister"
        >Register for this exam</button>
        <router-link
          v-if="subject.dsa_subject_code"
          :to="`/student/attempts/${subject.dsa_subject_code}`"
          class="btn btn-outline"
        >View my attempts</router-link>
      </div>
    </template>
    <div v-else class="empty">Subject not found.</div>

    <div v-if="registerModal" class="modal-overlay" @click.self="registerModal = false">
      <div class="modal-card">
        <h3>Register for {{ subjectName }}</h3>
        <p>Select a Pass to complete registration:</p>
        <div v-if="passes.length === 0" class="empty pass-empty" style="margin:16px 0">
          No valid Pass available. Purchase a DSA Pass first.
        </div>
        <label v-for="p in passes" :key="p.pass_id" class="pass-option">
          <input v-model="selectedPassId" type="radio" :value="p.pass_id">
          {{ p.pass_name || 'DSA Pass' }} — {{ p.remaining }} remaining
        </label>
        <div class="actions" style="margin-top:20px">
          <button type="button" class="btn btn-outline" @click="registerModal = false">Cancel</button>
          <button type="button" class="btn btn-primary" :disabled="registering" @click="confirmRegister">Confirm</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getSubjectDetail, getPassWallet, registerStudentWithPass } from '@/api/dsa'
import { getUserId } from '@/utils/store'
import { subjectCategory, unwrapId } from '@/utils/dsaFormat'

export default {
  name: 'StudentCatalogSubject',
  data() {
    return {
      loading: true,
      subject: null,
      attemptCount: 0,
      maxAttempts: 3,
      canRegister: false,
      registered: false,
      registerModal: false,
      passes: [],
      selectedPassId: '',
      registering: false,
    }
  },
  computed: {
    subjectName() {
      if (!this.subject) return this.$route.params.subjectCode || 'Subject'
      return this.subject.dsa_subject_name || this.subject.project_ename || this.subject.dsa_subject_code
    },
  },
  watch: {
    '$route.params.subjectCode'() {
      this.load()
    },
  },
  mounted() {
    this.load()
  },
  methods: {
    subjectCategory,
    async load() {
      this.loading = true
      try {
        const code = this.$route.params.subjectCode
        const quarter = this.$route.query.quarter || ''
        const res = await getSubjectDetail(getUserId(), code, quarter)
        if (res.data && res.data.code === 0) {
          const data = res.data.data || {}
          this.subject = data.subject || null
          this.attemptCount = data.attempt_count || 0
          this.maxAttempts = data.max_attempts || 3
          this.canRegister = !!data.can_register
          this.registered = !!data.registered
        } else {
          this.subject = null
          this.$emit('toast', (res.data && res.data.msg) || 'Subject not found')
        }
      } catch (e) {
        this.$emit('toast', 'Failed to load subject')
      } finally {
        this.loading = false
      }
    },
    async openRegister() {
      const res = await getPassWallet(getUserId())
      const wallet = (res.data && res.data.data) || {}
      this.passes = (wallet.passes || []).filter(p => (p.remaining || 0) > 0)
      this.selectedPassId = this.passes.length === 1 ? this.passes[0].pass_id : ''
      this.registerModal = true
    },
    async confirmRegister() {
      if (!this.subject) return
      if (!this.selectedPassId) {
        this.registerModal = false
        this.$router.push('/student/pass')
        return
      }
      this.registering = true
      try {
        const project_id = unwrapId(this.subject) || this.subject.id
        const res = await registerStudentWithPass(getUserId(), project_id, this.selectedPassId)
        if (res.data && res.data.code === 0) {
          this.$emit('toast', 'Registration successful')
          this.registerModal = false
          this.load()
        } else {
          this.$emit('toast', (res.data && res.data.msg) || 'Registration failed')
        }
      } catch (e) {
        this.$emit('toast', 'Registration failed')
      } finally {
        this.registering = false
      }
    },
  },
}
</script>

<style scoped>
.meta-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:16px}
.meta-grid .label{font-size:12px;color:var(--muted);font-weight:500}
.meta-grid .val{font-size:15px;font-weight:600;margin-top:4px}
.desc{margin-top:18px;font-size:14px;color:var(--gray);line-height:1.55}
.desc.muted{color:var(--muted)}
.pass-option{display:block;margin:10px 0;font-size:14px}
.pass-empty{font-size:13.5px}
.modal-overlay{position:fixed;inset:0;background:rgba(0,0,0,.4);display:flex;align-items:center;justify-content:center;z-index:1000}
.modal-card{background:#fff;border-radius:14px;padding:24px;max-width:440px;width:90%}
@media(max-width:700px){.meta-grid{grid-template-columns:1fr 1fr}}
</style>
